import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A long list a page at a time (Dimi, 2026-10-08: infinite scroll, never pagination): the first page, then the next
 * each time the end scrolls into view. `load(before)` fetches the page after the row `before` (its id; none for the
 * first); `deps` start it over (a tab, a search). A page shorter than `pageSize` is the last.
 */
export function usePaged<T extends { id: string }>(load: (before: string | null) => Promise<T[]>, deps: unknown[], pageSize = 50) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  // One page in flight at a time: two sightings of the end before React re-renders must not fetch the same page twice.
  const inFlight = useRef(false);

  const fetchPage = useCallback(async (before: string | null, gen: number) => {
    inFlight.current = true;
    setLoading(true);
    try {
      const page = await load(before);
      if (gen !== generation.current) return;
      setRows((prev) => (before ? [...(prev ?? []), ...page] : page));
      setDone(page.length < pageSize);
      setError(null);
    } catch (err) {
      if (gen === generation.current) setError((err as Error).message);
    } finally {
      if (gen === generation.current) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, deps);

  const reload = useCallback(() => {
    const gen = ++generation.current;
    setRows(null);
    setDone(false);
    void fetchPage(null, gen);
  }, [fetchPage]);
  useEffect(reload, [reload]);

  const more = () => {
    if (inFlight.current || done || error || !rows?.length) return;
    void fetchPage(rows[rows.length - 1]!.id, generation.current);
  };
  return { rows, done, loading, error, more, reload };
}

/**
 * The end of a paged list: asks for the next page as it comes into view, says "Loading…" while it does. It looks
 * again each time a page has landed, so a page too short to push it out of view still brings the next one.
 */
export function MoreOnScroll({ onMore, done, loading }: { onMore: () => void; done: boolean; loading: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef(onMore);
  latest.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (done || !el) return;
    const seen = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && latest.current(), { rootMargin: "400px" });
    seen.observe(el);
    return () => seen.disconnect();
  }, [done, loading]);
  if (done) return null;
  return <div ref={ref} className="py-4 text-center text-xs text-faint">{loading ? "Loading…" : ""}</div>;
}
